import { defineStore } from 'pinia'
import { convertArrayToObject } from 'src/utils'
import { Platform } from 'quasar'
import { NotifyMethods } from 'src/boot/notify'
import { useAgeVerificationStore } from 'src/stores/age-verification'
import { useCustomerStore } from 'src/stores/customer'
import { useAuthStore } from 'src/stores/auth'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'

export const useProductStore = defineStore('product', {
  state: () => ({
    details: {},
    seoMapping: {},
    loading: false,
    companionProducts: {},
    sortByOptions: [
      { value: 'Price Low to High', label: 'Price Low to High' },
      { value: 'Price High to Low', label: 'Price High to Low' },
      { value: 'Best Sellers', label: 'Best Sellers' },
      { value: 'Customer Reviews', label: 'Customer Reviews (Ratings)' },
    ],
    pagination: {
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      totalPages: 0,
    },
    paginationByScope: {},
    activePaginationScope: '',
    currentSelectedProductVariant: {},
  }),
  getters: {
    getProductById(state) {
      return (productId) => state.details[productId]
    },
    getProductBySeoName(state) {
      return (seoName) => {
        let productId = state.seoMapping[seoName]
        return state.details[productId] || {}
      }
    },
    getCompanionProductsById(state) {
      return (ProductSeoName) => {
        let companionProduct = state.companionProducts[ProductSeoName]
        let inStockProduct = {}
        for (let productId in companionProduct) {
          if (
            companionProduct &&
            companionProduct[productId].isinstock == true
          ) {
            inStockProduct[productId] = companionProduct[productId]
          }
        }
        // return state.companionProducts[ProductSeoName]
        return inStockProduct
      }
    },
  },
  actions: {
    defaultPagination() {
      return {
        totalCount: 0,
        pageSize: 20,
        currentPage: 1,
        totalPages: 0,
      }
    },
    async getProductDetail(params) {
      try {
        this.loading = true
        const ageVerificationStore = useAgeVerificationStore(),
          customerStore = useCustomerStore(),
          authStore = useAuthStore(),
          persistedStore = usePersistedStore()

        let fParams = {
          ...params,
        }

        if (
          ageVerificationStore.skipBrowsingAgeRestrictedProd &&
          ((!authStore.isLoggedIn &&
            !persistedStore.eaivPersistedVerificationSuccess) ||
            (authStore.isLoggedIn && !customerStore.customer?.isVerifiedOnline))
        ) {
          fParams['skipBrowsingAgeRestrictedProd'] = true
        }

        let response = await this.$axiosInstance.get('/Product/Details', {
          params: fParams,
        })
        let product = { ...response.data.data }

        for (let variant of product.variants) {
          if (variant.productVariantValueID == product.productID) {
            product.productVariantValues = variant.productVariantValues
            break
          }
        }

        if (product && product.productID) {
          this.seoMapping = Object.assign(
            {},
            { [product.seoName]: product.productID }
          )
          this.details = Object.assign({}, this.details, {
            [product.productID]: product,
          })
        }

        this.loading = false

        if (Platform?.is?.mobile && product.title) {
          const commonStore = useCommonStore()
          commonStore.setHeaderTitle({ title: product.title })
        }

        return product
      } catch (error) {}
    },
    async getSubstituteProducts({ params }) {
      try {
        let response = await this.$axiosInstance.get('/Product/Substitutes', {
          params,
        })
        return response.data.data
      } catch (error) {}
    },
    async getCompanionProducts(params) {
      try {
        let response = await this.$axiosInstance.get('/Product/Companions', {
            params,
          }),
          companionProducts = convertArrayToObject(
            response.data.data,
            'productID'
          )

        this.companionProducts = Object.assign({}, this.companionProducts, {
          [params.ProductSeoName]: companionProducts,
        })
      } catch (error) {}
    },
    async getFrequentSearch() {
      const commonStore = useCommonStore()
      this.$axiosInstance
        .get(`/Search/Frequent`, {
          params: {
            Page: 1,
            PageSize: 5,
            IsAuto: true,
            global: true,
          },
        })
        .then((response) => {
          let searchKeys = Object.keys(
            convertArrayToObject(response.data.data.items, 'keyword')
          )
          commonStore.setFrequentSearches(searchKeys)
        })
    },
    async getTrendingProducts() {
      const persistedStore = usePersistedStore()
      const commonStore = useCommonStore()

      this.$axiosInstance
        .get('v1/Product/Trending', {
          params: {
            Page: 1,
            PageSize: 5,
            global: true,
            fulfillmentCodeName: persistedStore.selectedFulfillmentCodeName,
            zipCode: persistedStore.locationOrShippingZip,
          },
        })
        .then((response) => {
          let trendingProducts = convertArrayToObject(
            response.data.data.items,
            'productID'
          )

          commonStore.setTrendingProducts(trendingProducts)
        })
    },
    async getByKeyword(rParams) {
      const ageVerificationStore = useAgeVerificationStore(),
        customerStore = useCustomerStore(),
        authStore = useAuthStore(),
        persistedStore = usePersistedStore()

      try {
        let params = {
          ...rParams,
          locationID: persistedStore.currentLocationId,
          zipCode: persistedStore.locationOrShippingZip,
        }
        if (
          ageVerificationStore.skipBrowsingAgeRestrictedProd &&
          ((!authStore.isLoggedIn &&
            !persistedStore.eaivPersistedVerificationSuccess) ||
            (authStore.isLoggedIn && !customerStore.customer?.isVerifiedOnline))
        ) {
          params['skipBrowsingAgeRestrictedProd'] = true
        }

        let { data } = await this.$axiosInstance.get('/Product/SearchKeyword', {
          params,
        })

        return data.data
      } catch (error) {
        return {}
      }
    },
    async getProducts(params) {
      const ageVerificationStore = useAgeVerificationStore(),
        customerStore = useCustomerStore(),
        authStore = useAuthStore(),
        persistedStore = usePersistedStore()

      try {
        let fParams = {
          ...params,
          global: true,
          zipCode: persistedStore.selectedZipCode,
        }

        if (
          ageVerificationStore.skipBrowsingAgeRestrictedProd &&
          ((!authStore.isLoggedIn &&
            !persistedStore.eaivPersistedVerificationSuccess) ||
            (authStore.isLoggedIn && !customerStore.customer?.isVerifiedOnline))
        ) {
          fParams['skipBrowsingAgeRestrictedProd'] = true
        }

        fParams['zipCode'] = persistedStore.locationOrShippingZip
        let response = await this.$axiosInstance.get('/Product', {
          params: fParams,
        })

        return response.data.data
      } catch (error) {
        // if (this.$router.currentRoute.name !== 'Home') this.$router.push({ name: 'Home' }).catch(() => {})
        if (error.errors && error.errors[0])
          NotifyMethods.showError(
            `<strong>Error!</strong>${error.errors[0].descreption}.`
          )
      }
    },
    async getReviewsByProduct(params) {
      try {
        let response = await this.$axiosInstance.get(
          '/CustomerReview/Product',
          {
            params,
          }
        )

        return response.data.data
      } catch (error) {}
    },
    async getReviewsSummary(params) {
      try {
        let response = await this.$axiosInstance.get(
          '/CustomerReview/Summary',
          {
            params,
          }
        )

        return response.data.data
      } catch (error) {}
    },
    async getAvailableNearByStore(params) {
      try {
        let response = await this.$axiosInstance.get(
          '/Product/AvailableNearByStore',
          { params }
        )
        return response.data
      } catch (error) {}
    },
    async getProductAvailability(params) {
      try {
        let { data } = await this.$axiosInstance.get(
          '/Product/AvailableByZipcode',
          { params }
        )
        return data
      } catch (error) {
        return error
      }
    },
    setPaginationScope(scopeKey = '') {
      const scopedKey = scopeKey || 'default'
      this.activePaginationScope = scopedKey
      this.pagination =
        this.paginationByScope[scopedKey] || this.defaultPagination()
    },
    updatePagination(pagination, scopeKey = '') {
      const scopedKey = scopeKey || this.activePaginationScope || 'default'
      const nextPagination = pagination || this.defaultPagination()
      this.paginationByScope = {
        ...this.paginationByScope,
        [scopedKey]: nextPagination,
      }
      if (this.activePaginationScope === scopedKey) {
        this.pagination = nextPagination
      }
    },
    resetPagination(scopeKey = '') {
      const scopedKey = scopeKey || this.activePaginationScope || ''
      const emptyPagination = this.defaultPagination()

      if (!scopedKey) {
        this.paginationByScope = {}
        this.pagination = emptyPagination
        this.activePaginationScope = ''
        return
      }

      this.paginationByScope = {
        ...this.paginationByScope,
        [scopedKey]: emptyPagination,
      }
      if (this.activePaginationScope === scopedKey) {
        this.pagination = emptyPagination
      }
    },
    setState({ key, value }) {
      if (key) this[key] = value
    },
    setLoading(loading) {
      this.loading = loading
    },
    async getProductAttributes(params) {
      try {
        let response = await this.$axiosInstance.get(
          '/Product/ProductAttributes',
          { params }
        )

        return response.data.data
      } catch (error) {}
    },
    async setProductNotification(body) {
      try {
        let { data } = await this.$axiosInstance.post('v1/Cart/NotifyMe', body)
        return data
      } catch (error) {
        return error
      }
    },
  },
})
